<script lang="ts">
	import { ContextMenuButton } from "context-menu"
	import { DataHandlerMedia, type Media } from "luxedo-data"
	import { RenderProgressIndicator } from "../rendering"
	import { mediaCompletionMarkerOf, mediaProgressHandler } from "./MediaProgressHandler.svelte"
	import { LuxedoRPC } from "luxedo-rpc"
	import { Toast } from "toaster"
	import { TileLabel } from "../file-system"
	import { Reactivity } from "luxedo-reactivity"

	type EventHandler<T extends Event> = (
		e: T & {
			currentTarget: EventTarget
		},
	) => void

	interface Props {
		media: Media
		dragging?: boolean
		selected?: boolean
		onclick?: EventHandler<MouseEvent>
		ondblclick?: EventHandler<MouseEvent>
		oncontextmenu?: EventHandler<MouseEvent>
		ondragend?: EventHandler<DragEvent>
		ondragstart?: EventHandler<DragEvent>
		ontouchstart?: EventHandler<TouchEvent>
		ontouchend?: EventHandler<TouchEvent>
		ontouchcancel?: EventHandler<TouchEvent>
	}

	let {
		media,
		dragging,
		selected,
		onclick,
		ondblclick,
		oncontextmenu,
		ondragend,
		ondragstart,
		ontouchcancel,
		ontouchend,
		ontouchstart,
	}: Props = $props()

	// Failed only while the media still carries the completion marker it had when the failure
	// was recorded: a run that finished later (a slow upload that completed, a job re-run from
	// another session) moves updated_at and clears the badge. `!media.size` could not tell those
	// apart for a re-uploaded media, which already has a size (PR #1052 review).
	// Read through Reactivity so a pull that moves updated_at in place re-evaluates this: a plain
	// class field on a keyed-list prop never would (review council, 2026-09-24).
	let processingFailed = $derived(
		mediaProgressHandler.progress[media.id]?.status === "fail" &&
			mediaProgressHandler.progress[media.id]?.failedMarker ===
				mediaCompletionMarkerOf(Reactivity.propOf(media, "updated_at")),
	)
	let isProcessing = $derived(mediaProgressHandler.progress[media.id]?.status === "processing")
	let isQueued = $derived(mediaProgressHandler.progress[media.id]?.status === "queued")
	let progress = $derived(mediaProgressHandler.progress[media.id]?.progress)

	$effect.pre(() => {
		Reactivity.useProps(media, ["src", "updated_at"])
	})

	let thumbnail = $derived(Reactivity.propOf(media, "src").thumbnail)

	/** Re-run processing for a media whose source is still there, instead of uploading it again. */
	let retrying = $state(false)

	async function retryProcessing(event: MouseEvent) {
		event.stopPropagation()
		if (retrying) return
		retrying = true
		try {
			// Started first, then watched: watching immediately polls the registry, and until the
			// server has been asked there is no record yet — which the poll reads as "finished".
			await LuxedoRPC.api.media.media_process_begin(media.id)
		} catch (err) {
			console.error("Could not retry media processing", err)
			Toast.error("Could not retry processing. Please try again.")
			return
		} finally {
			// Always, so a failed retry does not leave the button disabled for good. On the success
			// path the tile stops showing Failed a moment later and the button unmounts anyway.
			retrying = false
		}
		mediaProgressHandler.setTemporaryProgress(media.id, 0)
	}

	async function onRename(newName: string) {
		try {
			await DataHandlerMedia.rename(media, newName)
			Toast.success("Media renamed.")
		} catch (e) {
			console.error("[ERROR] ", e)

			Toast.error("There was an error renaming this media, please refresh and try again.")
		}
	}
</script>

<div
	class="tile-container"
	class:dragging
	class:selected
	id="media-tile-{media.id}"
	role="button"
	title={media.name}
	tabindex="0"
	onkeydown={() => {}}
	{onclick}
	{ondblclick}
	{oncontextmenu}
	{ondragend}
	{ondragstart}
	{ontouchcancel}
	{ontouchend}
	{ontouchstart}
	draggable={true}
>
	{#if oncontextmenu}
		<div class="ctx-menu-btn">
			<ContextMenuButton onClick={oncontextmenu} />
		</div>
	{/if}
	<div class="tile-icon-container">
		<div class="tile">
			{#if isProcessing}
				<RenderProgressIndicator {progress} />
			{:else if isQueued}
				<!-- role="status" so the swap is announced; it changes nothing visually. The label
				     stays the single word (Josiah, 2026-09-22) -- the tile is narrow and a longer
				     one wrapped badly. -->
				<span class="queued-render" title="Waiting for a processing slot on your account" role="status">
					Queued
				</span>
			{:else if processingFailed}
				<span class="failed-render" role="alert"> ⚠ Processing failed </span>
				<button
					class="retry-processing"
					type="button"
					onclick={retryProcessing}
					ondblclick={(e) => e.stopPropagation()}
					disabled={retrying}
					title="Process this file again"
					aria-label="Retry processing {media.name}"
				>
					{retrying ? "Retrying…" : "Retry"}
				</button>
			{:else}
				<img src={thumbnail} alt="{media.name} Thumbnail" />
			{/if}
		</div>
	</div>
	<div class="tile-label-container">
		<TileLabel file={media} {onRename} />
		<span class="media-type">
			{media.fileType}
		</span>
	</div>
</div>


<style>
	.tile-container.selected :global(.tile-label) {
		color: var(--color-main);
	}

	.tile-container .tile-icon-container {
		border-radius: var(--br);
		outline-color: var(--color-main);
		outline-style: solid;
		outline-width: 0px;

		transition: outline-width var(--transition-duration) var(--transition-timing);
		box-sizing: border-box;
	}

	.tile-container.selected .tile-icon-container {
		outline-width: 4px;
	}

	.tile {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}

	.queued-render {
		line-height: 1;
		text-align: center;
		color: var(--color-text);
		font-size: var(--small);
		letter-spacing: 0.02em;
	}

	.failed-render {
		line-height: 1;
		text-align: center;
		color: var(--ct-red);
	}

	.retry-processing {
		margin-top: 0.7rem;
		padding: 0.1rem 0.4rem;
		font-size: var(--small);
		color: var(--color-text);
		background: transparent;
		border: 1px solid var(--color-text);
		border-radius: var(--br);
		cursor: pointer;
	}
</style>
